"use client";

import { t } from "@/lib/i18n/interpolate";
import type {
  CornellGeometry,
  CornellHeader,
  CornellRow,
} from "@/lib/tool-kit/study/cornell";

/*
 * What the Cornell notes template puts on paper: the blank ruled page (drawn as SVG in
 * millimetres, so the preview and the printout are one drawing) and the online notes
 * laid out for printing. Every word comes in through `labels`.
 */

const INK = "#1f1f1f";
const MUTED = "#5f5f5f";
const RULE = "#a9bfd1";
const MARGIN_RULE = "#d55e00";

export interface CornellSheetLabels {
  name: string;
  course: string;
  date: string;
  topic: string;
  cues: string;
  notes: string;
  summary: string;
  /** The preview's accessible name. */
  preview: string;
  /** "{label}:" — a header field's caption on the printed notes. */
  field: string;
}

/** One page as SVG in millimetres: the same drawing for the preview and the printout. */
export function CornellPage({
  geometry,
  header,
  labels,
  print,
}: {
  geometry: CornellGeometry;
  header: CornellHeader;
  labels: CornellSheetLabels;
  /** Millimetre-sized for the printer, or fluid for the on-screen preview. */
  print: boolean;
}) {
  const { width, height, cueWidth, bodyTop, summaryTop } = geometry;
  const fields: [string, string, number, number][] = [
    [labels.name, header.name, 0, 7],
    [labels.course, header.course, width / 2, 7],
    [labels.date, header.date, 0, 15],
    [labels.topic, header.topic, width / 2, 15],
  ];
  return (
    <svg
      aria-label={print ? undefined : labels.preview}
      className={
        print ? "block break-after-page last:break-after-auto" : "h-auto w-full"
      }
      fontFamily="system-ui, -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif"
      height={print ? `${height}mm` : undefined}
      role={print ? undefined : "img"}
      viewBox={`0 0 ${width} ${height}`}
      width={print ? `${width}mm` : undefined}
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect fill="#ffffff" height={height} width={width} />
      {fields.map(([label, value, x, y]) => (
        <g key={label}>
          <text fill={MUTED} fontSize={3.2} x={x} y={y}>
            {label}
          </text>
          <line
            stroke={MUTED}
            strokeWidth={0.25}
            x1={x + 14}
            x2={x + width / 2 - 4}
            y1={y + 0.6}
            y2={y + 0.6}
          />
          {value ? (
            <text fill={INK} fontSize={3.8} x={x + 15} y={y - 0.4}>
              {value}
            </text>
          ) : null}
        </g>
      ))}
      <line
        stroke={INK}
        strokeWidth={0.5}
        x1={0}
        x2={width}
        y1={bodyTop}
        y2={bodyTop}
      />
      {geometry.bodyLines.map((y) => (
        <line
          key={y}
          stroke={RULE}
          strokeWidth={0.2}
          x1={0}
          x2={width}
          y1={y}
          y2={y}
        />
      ))}
      <line
        stroke={MARGIN_RULE}
        strokeWidth={0.4}
        x1={cueWidth}
        x2={cueWidth}
        y1={bodyTop}
        y2={summaryTop}
      />
      <text fill={MUTED} fontSize={3} x={1} y={bodyTop + 4.2}>
        {labels.cues}
      </text>
      <text fill={MUTED} fontSize={3} x={cueWidth + 2} y={bodyTop + 4.2}>
        {labels.notes}
      </text>
      <line
        stroke={INK}
        strokeWidth={0.5}
        x1={0}
        x2={width}
        y1={summaryTop}
        y2={summaryTop}
      />
      <text fill={MUTED} fontSize={3} x={1} y={summaryTop + 4.2}>
        {labels.summary}
      </text>
      {geometry.summaryLines.map((y) => (
        <line
          key={`s-${y}`}
          stroke={RULE}
          strokeWidth={0.2}
          x1={0}
          x2={width}
          y1={y}
          y2={y}
        />
      ))}
    </svg>
  );
}

/** The online notes on paper: header, cue and note side by side, summary box. */
export function PrintedNotes({
  header,
  rows,
  summary,
  labels,
}: {
  header: CornellHeader;
  rows: readonly CornellRow[];
  summary: string;
  labels: CornellSheetLabels;
}) {
  const filled = rows.filter(
    (row) => row.cue.trim() !== "" || row.note.trim() !== ""
  );
  const fields: [string, string][] = [
    [labels.name, header.name],
    [labels.course, header.course],
    [labels.date, header.date],
    [labels.topic, header.topic],
  ];
  return (
    <div className="hidden text-black print:block">
      <div className="mb-3 grid grid-cols-2 gap-x-6 gap-y-1 border-black border-b pb-2 text-[10pt]">
        {fields.map(([label, value]) => (
          <p key={label}>
            <span className="text-gray-600">{t(labels.field, { label })}</span>{" "}
            {value}
          </p>
        ))}
      </div>
      <table className="w-full border-collapse text-[11pt]">
        <thead>
          <tr>
            <th className="w-[30%] border-[#d55e00] border-r py-1 pr-3 text-left font-semibold">
              {labels.cues}
            </th>
            <th className="py-1 pl-3 text-left font-semibold">
              {labels.notes}
            </th>
          </tr>
        </thead>
        <tbody>
          {filled.map((row) => (
            <tr
              className="break-inside-avoid border-gray-300 border-t align-top"
              key={row.id}
            >
              <td className="whitespace-pre-wrap border-[#d55e00] border-r py-2 pr-3">
                {row.cue}
              </td>
              <td className="whitespace-pre-wrap py-2 pl-3">{row.note}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="mt-4 break-inside-avoid border-black border-t-2 pt-2">
        <p className="font-semibold text-[11pt]">{labels.summary}</p>
        <p className="whitespace-pre-wrap text-[11pt]">{summary}</p>
      </div>
    </div>
  );
}
